import React from 'react';
import Icon from '../../../components/AppIcon';

const DiscussionQuestions = ({ summaryData, primaryConcern }) => {
  const formatLabel = (str) => str?.replace(/_/g, ' ')?.replace(/\b\w/g, c => c?.toUpperCase());

  const generateQuestions = () => {
    const questions = [];

    // Q1: Based on primary symptom
    if (primaryConcern) {
      const severity = primaryConcern?.avgSeverity;
      const symptomName = formatLabel(primaryConcern?.name);
      const location = formatLabel(primaryConcern?.location);
      if (severity >= 7) {
        questions?.push(`My ${symptomName} in the ${location} has been rated ${primaryConcern?.avgSeverity}/10 on average. What could be causing this level of severity and what investigations would you recommend?`);
      } else {
        questions?.push(`I've been experiencing ${symptomName} in the ${location} with an average severity of ${primaryConcern?.avgSeverity}/10. Is this something we should investigate further?`);
      }
    } else {
      questions?.push('Based on my symptom history, what do you think could be the underlying cause of my symptoms?');
    }

    // Q2: Based on trend
    const increasingSymptoms = summaryData?.trends?.filter(t => t?.direction === 'increasing');
    if (increasingSymptoms?.length > 0) {
      const symptomNames = increasingSymptoms?.slice(0, 2)?.map(t => formatLabel(t?.symptom))?.join(' and ');
      questions?.push(`My ${symptomNames} appear to be getting worse over time. Should I be concerned, and are there any lifestyle changes or treatments that could help?`);
    } else if (primaryConcern) {
      questions?.push(`My ${formatLabel(primaryConcern?.name)} has remained persistent. What are the next steps to manage or resolve this?`);
    }

    // Q3: Based on triggers
    if (summaryData?.triggers?.length > 0) {
      const triggerList = summaryData?.triggers?.slice(0, 3)?.join(', ');
      questions?.push(`I've noticed my symptoms seem to be triggered by ${triggerList}. Can you help me understand the connection and how to manage these triggers?`);
    } else {
      questions?.push("I haven't been able to identify clear triggers for my symptoms. What tests or approaches could help identify the cause?");
    }

    // Q4: Based on daily impact
    const highImpacts = summaryData?.impacts?.filter(i => i?.score >= 6);
    if (highImpacts?.length > 0) {
      const impactNames = highImpacts?.slice(0, 2)?.map(i => formatLabel(i?.category))?.join(' and ');
      questions?.push(`My symptoms are significantly affecting my ${impactNames}. What treatment options are available to improve my quality of life?`);
    } else if (summaryData?.medications?.length > 0) {
      questions?.push('Are my current medications appropriate for managing these symptoms, and are there any adjustments or alternatives I should consider?');
    } else {
      questions?.push('What treatment options, medications, or referrals would you recommend based on my symptom history?');
    }

    return questions?.slice(0, 4);
  };

  const questions = generateQuestions();

  return (
    <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-7 h-7 bg-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
          <Icon name="MessageSquare" size={14} className="text-white" />
        </div>
        <h3 className="text-sm font-bold text-indigo-900 uppercase tracking-wide">Suggested Discussion Questions</h3>
      </div>
      <p className="text-xs text-indigo-600 mb-4">Auto-generated based on your symptom data — bring these to your appointment:</p>
      <div className="space-y-3">
        {questions?.map((question, index) => (
          <div key={index} className="flex items-start gap-3 bg-white rounded-lg p-3 border border-indigo-100">
            <span className="flex-shrink-0 w-6 h-6 bg-indigo-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
              {index + 1}
            </span>
            <p className="text-sm text-gray-800 leading-relaxed">{question}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DiscussionQuestions;
